<template>
  <v-card class="overview-card">
    <v-card-title>
      <div class="card-header flex justify-space-between">
        {{ title }}
        <div class="d-flex" v-if="!disableSwitch">
          <v-btn
            :disabled="switchItem.data.length === 0"
            class="mr-3"
            x-small
            :text="index !== selectedSwitchItemIndex"
            :key="index"
            :dark="index === selectedSwitchItemIndex"
            v-for="(switchItem, index) in switchData"
            @click="selectedSwitchItemIndex = index"
          >
            {{ switchItem.label }}
          </v-btn>
        </div>
      </div>
    </v-card-title>
    <v-card-text>
      <!-- Serien-Gruppen: gemeinsam ein-/ausblenden statt 7 Einzel-Legenden -->
      <div v-if="activeGroups.length" class="series-groups">
        <button
          v-for="group in activeGroups"
          :key="group.label"
          type="button"
          class="series-groups__chip"
          :class="{ 'series-groups__chip--off': hiddenGroups.includes(group.label) }"
          @click="toggleGroup(group.label)"
        >
          <span class="series-groups__dot" :style="{ background: group.color }" />
          {{ group.label }}
        </button>
      </div>
      <BarChart
        :aspect-ratio="aspectRatio"
        :key="selectedSwitchItemIndex"
        :data="chartData"
        :unit="chartUnit"
        :stacked="activeStacked"
        :index-tooltip="activeIndexTooltip"
        :tooltip-footer="activeTooltipFooter"
        :hide-legend="activeGroups.length > 0"
        :hidden-datasets="hiddenDatasetLabels"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import BarChart from "@/components/BarChart";

export default {
  name: "OverviewBarCard",
  components: { BarChart },
  data() {
    return {
      selectedSwitchItemIndex: 0,
      // Default: Fokus auf Deckungsbeitrag + Vorperiode — Kosten/Umsatz zuschaltbar
      hiddenGroups: ["Kosten", "Umsatz"],
    };
  },
  props: {
    title: String,
    data: Object,
    switchData: Array,
    unit: String,
    disableSwitch: {
      default: false
    },
    aspectRatio: {
      default: 3
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    indexTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipFooter: {
      type: Function,
      default: null,
    },
    groups: {
      type: Array,
      default: null,
    },
  },
  methods: {
    toggleGroup(label) {
      this.hiddenGroups = this.hiddenGroups.includes(label)
        ? this.hiddenGroups.filter((l) => l !== label)
        : [...this.hiddenGroups, label];
    },
  },
  computed: {
    activeSwitchItem() {
      return this.switchData && !this.disableSwitch
        ? this.switchData[this.selectedSwitchItemIndex]
        : null;
    },
    activeStacked() {
      return this.activeSwitchItem?.stacked ?? this.stacked;
    },
    activeIndexTooltip() {
      return this.activeSwitchItem?.indexTooltip ?? this.indexTooltip;
    },
    activeTooltipFooter() {
      return this.activeSwitchItem?.tooltipFooter ?? this.tooltipFooter;
    },
    activeGroups() {
      return this.activeSwitchItem?.groups || this.groups || [];
    },
    hiddenDatasetLabels() {
      return this.activeGroups
        .filter((group) => this.hiddenGroups.includes(group.label))
        .flatMap((group) => group.datasetLabels);
    },
    chartData() {
      return this.disableSwitch ? this.data : this.switchData ? this.switchData[this.selectedSwitchItemIndex].data : this.data;
    },
    chartUnit() {
      if (this.switchData && !this.disableSwitch) {
        return this.switchData[this.selectedSwitchItemIndex].unit || this.unit;
      } else {
        return this.unit;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.series-groups {
  display: flex;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 12px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.7);
    transition: all 0.15s ease;

    &--off {
      opacity: 0.45;
      text-decoration: line-through;
    }
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }
}
</style>

<style lang="scss" scoped>
.overview-card {
  width: 100%;
  height: 100%;
  box-shadow: rgba(0, 0, 0, 0.07) 0 1px 2px, rgba(0, 0, 0, 0.07) 0 2px 4px, rgba(0, 0, 0, 0.07) 0 4px 8px, rgba(0, 0, 0, 0.07) 0 8px 16px, rgba(0, 0, 0, 0.07) 0 16px 32px, rgba(0, 0, 0, 0.07) 0 32px 64px !important;

  @include md() {
    box-shadow: rgba(14, 63, 126, 0.04) 0 0 0 1px, rgba(42, 51, 69, 0.04) 0 1px 1px -0.5px, rgba(42, 51, 70, 0.04) 0 3px 3px -1.5px, rgba(42, 51, 70, 0.04) 0 6px 6px -3px, rgba(14, 63, 126, 0.04) 0 12px 12px -6px, rgba(14, 63, 126, 0.04) 0 24px 24px -12px !important;
  }

  .card-header {
    height: 52px;
  }
}
</style>
